import React, { useState } from "react";

import Cookies from "js-cookie";
import { isEmpty } from "lodash";
import { FormProvider, useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import * as yup from "yup";

import useGlobalSnackbar from "@/hooks/useGlobalSnackbar";
import api from "@/utils/api";
import { displayError } from "@/utils/errors";
import { gtagEvent } from "@/utils/gtag";

import LoginForm from "./LoginForm";

const validationSchema = yup
  .object({
    email: yup.string().label("Email").email().required(),
    password: yup.string().label("Password").required(),
    otp_attempt: yup.string().label("OTP code").required(),
  })
  .defined();

export type FormValues = yup.InferType<typeof validationSchema>;

const LoginPage: React.FC = () => {
  const navigate = useNavigate();
  const snackbar = useGlobalSnackbar();
  const [isLookUpSucsess, setIsLookUpSuccess] = useState<boolean>(false);

  const methods = useForm({
    defaultValues: {
      email: "",
      password: "",
      otp_attempt: "",
    },
  });

  async function lookUp(values: FormValues) {
    const { email, password } = values;
    try {
      const response = await api.post("coach_accounts/sessions/look_up", {
        coach_account: {
          email: email,
          password: password,
        },
      });

      if (response.ok) {
        setIsLookUpSuccess(true);
      } else {
        const json = await response.json();
        snackbar.open(displayError(json));
      }
    } catch (e) {
      snackbar.open(displayError(e));
    }
  }

  async function login(values: FormValues) {
    const { email, password, otp_attempt } = values;
    try {
      const response = await api.post("coach_accounts/sign_in", {
        coach_account: {
          email: email,
          password: password,
          otp_attempt: otp_attempt,
        },
      });

      if (response.ok) {
        const authHeader = response.headers.get("Authorization") ?? "";
        const [, token] = authHeader?.split(" ");
        Cookies.set("token", token, { secure: true });
        gtagEvent("login");
        navigate("/", { replace: true });
      } else {
        snackbar.open("Incorrect or expired code.");
      }
    } catch (e) {
      snackbar.open(displayError(e, "Failed to login, please try again"));
    }
  }

  async function handleSubmit(values: FormValues) {
    if (isEmpty(values.otp_attempt)) return await lookUp(values);
    return await login(values);
  }

  async function handleOnResend() {
    const values = methods.getValues();
    return await lookUp(values);
  }

  return (
    <form onSubmit={methods.handleSubmit(handleSubmit)}>
      <FormProvider {...methods}>
        <LoginForm
          isLookUpSuccess={isLookUpSucsess}
          onResend={handleOnResend}
        />
      </FormProvider>
    </form>
  );
};

export default LoginPage;
